import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { helpService } from '@/shared/help/help.service';
import type { HelpContent, HelpKind } from '@/shared/help/types';

export const helpQueryKeys = {
    all:       ['help'] as const,
    admin:     () => ['help', 'admin'] as const,
    published: () => ['help', 'published'] as const,
} as const;

/** Help content for the client-facing panel — published entries only. */
export function usePublishedHelpQuery(enabled = true) {
    return useQuery<HelpContent>({
        queryKey:  helpQueryKeys.published(),
        queryFn:   () => helpService.listPublished(),
        // Rarely changes, and the panel is opened on demand.
        staleTime: 5 * 60_000,
        enabled,
    });
}

/** Help content for the admin editor — includes hidden entries. */
export function useAdminHelpQuery() {
    return useQuery<HelpContent>({
        queryKey: helpQueryKeys.admin(),
        queryFn:  () => helpService.listAll(),
    });
}

/**
 * Create / update / delete for the admin editor.
 *
 * Every mutation invalidates BOTH cached views: the admin list the editor
 * renders, and the published list any open Help panel is showing — otherwise
 * hiding an article would leave it visible to the same session until reload.
 */
export function useHelpMutations() {
    const qc = useQueryClient();
    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: helpQueryKeys.all });
    };

    const create = useMutation({
        mutationFn: ({ kind, body }: { kind: HelpKind; body: Record<string, unknown> }) =>
            helpService.create(kind, body),
        onSuccess: invalidate,
    });

    const update = useMutation({
        mutationFn: ({ kind, id, body }: { kind: HelpKind; id: string; body: Record<string, unknown> }) =>
            helpService.update(kind, id, body),
        onSuccess: invalidate,
    });

    const remove = useMutation({
        mutationFn: ({ kind, id }: { kind: HelpKind; id: string }) => helpService.remove(kind, id),
        onSuccess: invalidate,
    });

    return { create, update, remove };
}
